import { type Trip, TripStatus } from "@/api.types.v2";
import { Badge } from "@/components/ui/badge";
import { LANE_ORDER, type LaneCounts, LANE_META } from "./task-type-meta";
import { TRIP_STATUS_META, shipmentStatusStyle } from "./status-meta";

/** Numeric load/capacity for the capacity bar, or null when capacity is
 * unknown (Vehicle.capacity is an untyped record — we read the first numeric
 * value; load is the trip's task count). Returns null → caller hides the bar. */
export function tripLoadCapacity(
  trip: Trip,
  taskCount: number
): { load: number; capacity: number } | null {
  const cap = trip.vehicle?.capacity;
  if (!cap) return null;
  const num = Object.values(cap).find(
    (v): v is number => typeof v === "number" && v > 0
  );
  if (!num) return null;
  return { load: taskCount, capacity: num };
}

function capacityColor(ratio: number): string {
  if (ratio >= 0.92) return "#dc2626";
  if (ratio >= 0.75) return "#d97706";
  return "#16a34a";
}

/** Load-vs-capacity bar (rail · driver card · assign preview). */
export function CapacityBar({
  load,
  capacity,
  compact,
}: {
  load: number;
  capacity: number;
  compact?: boolean;
}) {
  const r = Math.min(1, capacity > 0 ? load / capacity : 0);
  return (
    <div className="flex items-center gap-1.5" title={`${load}/${capacity}`}>
      <div
        className="relative overflow-hidden rounded-full bg-zinc-200"
        style={{ width: compact ? 42 : 54, height: 5 }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${r * 100}%`, background: capacityColor(r) }}
        />
      </div>
      <span className="text-[11px] font-semibold tabular-nums text-zinc-600">
        {load}/{capacity}
      </span>
    </div>
  );
}

/** Compact colored count chips, one per non-empty lane (the "MiniCounts"). */
export function LaneChips({
  counts,
  size = 16,
}: {
  counts: LaneCounts;
  size?: number;
}) {
  const lanes = LANE_ORDER.filter((k) => counts[k] > 0);
  if (lanes.length === 0) return null;
  return (
    <span className="flex items-center gap-1">
      {lanes.map((k) => (
        <Badge
          key={k}
          title={LANE_META[k].label}
          className="rounded border-0 font-bold tabular-nums text-white"
          style={{
            background: LANE_META[k].color,
            minWidth: size,
            height: size,
            fontSize: size * 0.62,
            padding: "0 3px",
          }}
        >
          {counts[k]}
        </Badge>
      ))}
    </span>
  );
}

export function StatusPill({ status }: { status: TripStatus }) {
  const m = TRIP_STATUS_META[status] ?? TRIP_STATUS_META[TripStatus.PLANNED];
  // shadcn Badge base (already rounded-full); pin the v2 size/weight + status
  // color. Consuming the shared primitive means Badge updates propagate here.
  return (
    <Badge
      className="border-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.label}
    </Badge>
  );
}

/** Shipment lifecycle status pill — shared by Lookup + waypoint detail. */
export function ShipmentStatusBadge({ status }: { status: string }) {
  const m = shipmentStatusStyle(status);
  return (
    <Badge
      className="border-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.label}
    </Badge>
  );
}
